import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Summary from '../organisms/Origination/Summary/Summary';
import { useState, useEffect } from 'react';
import { clientData } from '../types/interfaces';
import Card from '../components/card/Card';
import Tabs from '../components/tabs/Tabs';
import SummaryDocuments from '../organisms/Origination/Summary/Documents';
import Risk from './RiskAnalysis';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import { Toast, useToast } from '../components/toast/toast';
import {
  getApplication,
  sendApplication,
  updateApplication,
} from '../services/ApplicationService';
import { hasData, hasError, isSuccessfulResponse } from '../utils/Utils';
import { APPLICATION_STATUS, ROLES_CAP } from '../utils/Constants';
import Modal from '../components/modal/modal';
import P from '../components/texts/p/P';
import TextArea from '../components/textarea/TextArea';
import { initialClientData } from '../utils/Constants';
import Icon from '../components/icon/Icon';
import H from '../components/texts/h/H';
import { routes } from '../routes/routePaths';
import useRole from '../hooks/useRole';
import useStatus from '../hooks/useStatus';
import { cuitMask } from '../utils/Masks';

const Instrumentation = () => {
  const { cuit, id } = useParams();
  const { state } = useLocation();
  const { status, viewOnly } = state ? state : { status: '', viewOnly: false };
  const navigate = useNavigate();
  const [isClientDataLoaded, setIsClientDataLoaded] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientData>(initialClientData);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const [isSaving, _setIsSaving] = useState<boolean>(false);
  const role = localStorage.getItem('roles');
  const [_isSentApplicationOpen, setIsSentApplicationOpen] =
    useState<boolean>(false);
  const { isInstrumentator } = useRole();
  const { isRejected, isFinished } = useStatus({
    applicationStatus: parseInt(status),
  });

  useEffect(() => {
    if (cuit && id) {
      getApplication(cuit, id).then((response) => {
        if (hasData(response)) {
          setClientData(response.data);
        }
      });
    }
  }, [cuit, id]);

  const contentTabs = [
    {
      label: 'Solicitud',
      content: (
        <Summary
          setClientData={(e: any) => setClientData(e)}
          applicationStatus={parseInt(status)}
          isSaving={isSaving}
          clientData={clientData}
          onLoadClientData={(e: boolean) => setIsClientDataLoaded(e)}
          isClientDataLoaded={isClientDataLoaded}
          showButtons={false}
          viewOnly={viewOnly}
        />
      ),
    },
    {
      label: 'Análisis de riesgo',
      content: (
        <Risk
          showStepBar={false}
          hideCommiteInfo={viewOnly}
          showOnlyRiskResume={true}
        />
      ),
    },
    {
      label: 'Documentación completa',
      content: (
        <SummaryDocuments
          cuit={clientData.cuit}
          documents={clientData.files}
          legalEntity={clientData.basic_data.legal_entity}
        />
      ),
    },
  ];

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSendingApplication(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      id || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (isSuccessfulResponse(response)) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error inesperado. Intentá nuevamente.',
          id: 'application-toast',
          description: hasError(response).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: false,
          autoClose: 5000,
        });
      }
      setIsSendingApplication(false);
    });
  };

  const getApplicationInfo = () => {
    return {
      products: clientData.products,
      manual_total_amount: clientData.manual_total_amount,
      requested_amount: clientData.requested_amount,
      reason: clientData.reason,
      review: clientData.review,
      extra_comments: clientData.extra_comments,
      has_valid_corporate_composition:
        clientData.has_valid_corporate_composition,
      category_type_id: clientData.category_type_id,
    };
  };

  const handleSubmit = () => {
    setIsSendingApplication(true);
    sendApplication(cuit || '', getApplicationInfo(), id || '').then(
      (response) => {
        if (isSuccessfulResponse(response)) {
          useToast.success({
            title: 'Solicitud enviada correctamente.',
            id: 'application-toast',
            description: '',
            closeButton: false,
            autoClose: 5000,
          });
          setIsSentApplicationOpen(true);
        } else if (hasError(response).isAnyError) {
          const errorMessage = hasError(response).isClientError;
          useToast.error({
            title: hasError(response).isClientError
              ? 'Ya existe una solicitud existente para este cliente.'
              : 'Ocurrió un error inesperado. Intentá nuevamente.',
            id: 'application-toast',
            description: errorMessage,
            closeButton: false,
            autoClose: 5000,
          });
        }
        setIsSendingApplication(false);
      }
    );
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
    >
      {(role?.includes(ROLES_CAP.comite) ||
        role?.includes(ROLES_CAP.commercial) ||
        role?.includes(ROLES_CAP.instrumentator)) && (
        <>
          <Toast id='application-toast' />
          <Modal
            isOpen={isRejectApplication}
            onClose={() => setIsRejectApplication(false)}
            title={'Declinar o solicitar cambios'}
            primaryText='Solicitar cambios'
            primaryVariant='primary'
            secondaryText='Declinar solicitud'
            secondaryVariant='secondary'
            onSecondaryAction={() => {
              setIsRejectApplication(false);
              handleApplication(APPLICATION_STATUS.rejected.label);
            }}
            onPrimaryAction={() => {
              setIsRejectApplication(false);
              handleApplication(APPLICATION_STATUS.pending.label);
            }}
            children={renderRejectModal()}
          />
          <Card display='flex' margin={{ left: '2', bottom: '4' }} gap='10px'>
            <Icon
              name='chevron_left_outline'
              height={24}
              width={24}
              cursor='pointer'
              onClick={() => window.history.back()}
            />
            <H withMargin={false} type='2' weight='bold' modifier='strong'>
              Solicitud {clientData?.basic_data?.company_name} ({' '}
              {cuitMask(clientData.cuit)} )
            </H>
          </Card>
          <Tabs tabs={contentTabs} />
          {isInstrumentator && !viewOnly && (
            <Card
              display='flex'
              justifyContent='flex-end'
              margin={{ top: '4' }}
            >
              {!isRejected && !isFinished && (
                <Button
                  id='application-button-reject'
                  size='lg'
                  variant='secondary'
                  onClick={() => setIsRejectApplication(true)}
                  disabled={isSaving || isSendingApplication}
                >
                  {isSaving || isSendingApplication ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Declinar o solicitar cambios'
                  )}
                </Button>
              )}
              <Card width='180px' margin={{ left: '4' }}>
                <Button
                  id='client-button-approve'
                  size='lg'
                  variant='primary'
                  onClick={handleSubmit}
                  fullWidth
                >
                  Aprobar
                </Button>
              </Card>
            </Card>
          )}
        </>
      )}
    </Card>
  );
};

export default Instrumentation;
